import React, { useState, useRef, useEffect } from 'react';
import { 
  UploadOutlined, 
  DeleteOutlined, 
  DragOutlined, 
  EyeOutlined,
  LoadingOutlined,
  StarOutlined,
  StarFilled
} from '@ant-design/icons';
import type { Image } from '../../lib/api';

interface ImageUploadProps {
  images?: Image[];
  onImagesChange?: (images: Image[]) => void;
  onUpload?: (files: File[], metadata: ImageMetadata[]) => Promise<void>;
  onDelete?: (imageId: string) => Promise<void>;
  onReorder?: (imageIds: string[]) => Promise<void>;
  onSetFeatured?: (imageId: string | null) => Promise<void>;
  featuredImageId?: string;
  maxFiles?: number;
  isLoading?: boolean;
}

interface ImageMetadata {
  title: string;
  description?: string;
  altText: string;
  width?: number;
  height?: number;
}

// Helper function to extract image dimensions
const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };
    img.onerror = () => {
      reject(new Error('Failed to load image for dimension extraction'));
    };
    img.src = URL.createObjectURL(file);
  });
};

const ImageUpload: React.FC<ImageUploadProps> = ({
  images = [],
  onImagesChange,
  onUpload,
  onDelete,
  onReorder,
  onSetFeatured,
  featuredImageId,
  maxFiles = 50,
  isLoading = false
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [localFeaturedImageId, setLocalFeaturedImageId] = useState<string | null>(featuredImageId || null);
  const [updatingFeatured, setUpdatingFeatured] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync local state with prop changes
  useEffect(() => {
    setLocalFeaturedImageId(featuredImageId || null);
  }, [featuredImageId]);

  // Handle file selection
  const handleFileSelect = (files: File[]) => {
    if (files.length === 0) return;

    // Validate files
    const validFiles: File[] = [];
    const errors: string[] = [];

    files.forEach(file => {
      // Check file type
      if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/)) {
        errors.push(`${file.name}: Invalid file type. Only JPEG, PNG, and WebP are allowed.`);
        return;
      }

      // Check file size (50MB max)
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        errors.push(`${file.name}: File too large. Maximum size is 50MB.`);
        return;
      }

      validFiles.push(file);
    });

    // Check total file count
    if (images.length + validFiles.length > maxFiles) {
      errors.push(`Cannot upload ${validFiles.length} files. Maximum ${maxFiles} images allowed (currently have ${images.length}).`);
    }

    if (errors.length > 0) {
      alert('Upload errors:\n' + errors.join('\n'));
      return;
    }

    if (validFiles.length > 0) {
      handleUpload(validFiles);
    }
  };

  // Handle upload
  const handleUpload = async (files: File[]) => {
    if (!onUpload) return;

    setUploading(true);
    try {
      // Extract dimensions for each file
      console.log('🖼️ Extracting dimensions for', files.length, 'images...');
      const metadata: ImageMetadata[] = await Promise.all(
        files.map(async (file) => {
          try {
            const dimensions = await getImageDimensions(file);
            console.log(`📏 ${file.name}: ${dimensions.width}x${dimensions.height}`);
            
            return {
              title: file.name.replace(/\.[^/.]+$/, ''), // Remove file extension
              description: '',
              altText: file.name.replace(/\.[^/.]+$/, ''),
              width: dimensions.width,
              height: dimensions.height
            };
          } catch (error) {
            console.warn(`⚠️ Failed to extract dimensions for ${file.name}:`, error);
            // Fallback to metadata without dimensions
            return {
              title: file.name.replace(/\.[^/.]+$/, ''),
              description: '',
              altText: file.name.replace(/\.[^/.]+$/, '')
            };
          }
        })
      );

      console.log('📊 Final metadata:', metadata.map(m => ({ 
        title: m.title, 
        width: m.width, 
        height: m.height 
      })));

      await onUpload(files, metadata);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  // Handle drag events
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    handleFileSelect(files);
  };

  // Handle file input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleFileSelect(files);
    
    // Reset input value to allow re-selecting same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle image reordering via drag and drop
  const handleImageDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleImageDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleImageDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleImageDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    // Reorder images locally
    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);

    // Update parent state immediately for better UX
    if (onImagesChange) {
      onImagesChange(newImages);
    }

    // Call API to persist the new order
    if (onReorder) {
      try {
        const newOrder = newImages.map(img => img.id);
        await onReorder(newOrder);
      } catch (error) {
        console.error('Reorder failed:', error);
        // Revert on error
        if (onImagesChange) {
          onImagesChange(images);
        }
      }
    }

    setDraggedIndex(null);
  };

  // Handle image viewing (open in modal instead of new window)
  const handleViewImage = (imageUrl: string, imageTitle: string) => {
    // Create a modal overlay to view the image
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      cursor: pointer;
    `;
    
    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = imageTitle;
    img.style.cssText = `
      max-width: 90%;
      max-height: 90%;
      object-fit: contain;
      border-radius: 8px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    `;
    
    const closeButton = document.createElement('div');
    closeButton.innerHTML = '✕';
    closeButton.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      color: white;
      font-size: 24px;
      cursor: pointer;
      background: rgba(0, 0, 0, 0.5);
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    
    const close = () => {
      document.body.removeChild(overlay);
    };
    
    overlay.appendChild(img);
    overlay.appendChild(closeButton);
    overlay.onclick = close;
    closeButton.onclick = close;
    
    document.body.appendChild(overlay);
  };

  // Handle image deletion
  const handleDelete = async (imageId: string) => {
    if (!onDelete) return;

    if (window.confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      try {
        await onDelete(imageId);
      } catch (error) {
        console.error('Delete failed:', error);
        alert('Delete failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    }
  };

  // Handle setting featured image with two-state toggle
  const handleSetFeatured = async (imageId: string) => {
    if (!onSetFeatured) return;

    // Determine the action: if this image is already featured, remove featured status
    const isCurrentlyFeatured = localFeaturedImageId === imageId;
    const newFeaturedId = isCurrentlyFeatured ? null : imageId;
    
    // Optimistically update local state
    setLocalFeaturedImageId(newFeaturedId);
    setUpdatingFeatured(imageId);

    try {
      // Call API with the new featured image ID (null to remove featured status)
      await onSetFeatured(newFeaturedId);
      // Success - local state is already correct
    } catch (error) {
      // Revert local state on error
      setLocalFeaturedImageId(localFeaturedImageId);
      console.error('Set featured failed:', error);
      alert('Failed to set featured image: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setUpdatingFeatured(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
          dragActive
            ? 'border-primary-500 bg-primary-50'
            : uploading
            ? 'border-gray-300 bg-gray-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleInputChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading || isLoading}
        />
        
        <div className="text-center">
          {uploading ? (
            <>
              <LoadingOutlined className="text-4xl text-primary-500 mb-4" spin />
              <p className="text-lg font-medium text-gray-7">Uploading images...</p>
              <p className="text-sm text-gray-5">Please wait while your images are being uploaded</p>
            </>
          ) : (
            <>
              <UploadOutlined className="text-4xl text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-7">
                {dragActive ? 'Drop images here' : 'Upload Images'}
              </p>
              <p className="text-sm text-gray-5">
                Drag and drop images here, or click to select files
              </p>
              <p className="text-xs text-gray-4 mt-2">
                Supports JPEG, PNG, WebP • Max 50MB per file • Up to {maxFiles} images
              </p>
              <p className="text-xs text-gray-4">
                Currently: {images.length}/{maxFiles} images
              </p>
            </>
          )}
        </div>
      </div>

      {/* Images Grid */}
      {images.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-9 mb-4">
            Uploaded Images ({images.length})
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {images.map((image, index) => (
              <div
                key={image.id}
                className={`relative bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-move ${
                  draggedIndex === index ? 'opacity-50' : ''
                } ${
                  dragOverIndex === index ? 'ring-2 ring-primary-500 ring-opacity-50' : ''
                }`}
                draggable
                onDragStart={(e) => handleImageDragStart(e, index)}
                onDragOver={(e) => handleImageDragOver(e, index)}
                onDragLeave={handleImageDragLeave}
                onDrop={(e) => handleImageDrop(e, index)}
              >
                {/* Image Preview */}
                <div className="aspect-video bg-gray-100 relative">
                  <img
                    src={image.url}
                    alt={image.alt || image.title || `Image ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      // Prevent infinite loops by only setting placeholder once
                      if (!target.src.includes('data:image/svg+xml')) {
                        console.warn('Failed to load image:', image.url);
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNCAyaDE2YTIgMiAwIDAgMSAyIDJ2MTJhMiAyIDAgMCAxLTIgMkg0YTIgMiAwIDAgMS0yLTJWNGEyIDIgMCAwIDEgMi0yeiIgZmlsbD0iI2Y0ZjRmNCIvPjxwYXRoIGQ9Im04LjUgMTEuNS0uODY2LS41YTEUI0IDAgMCAwLTEuNTM0IDBMNiAxMS41bDMuNSAzLjUgMS41LTEuNUwxNSAxN0g5bC0uNS0uNXoiIGZpbGw9IiNjYmNiY2IiLz48L3N2Zz4=';
                      }
                    }}
                  />
                  
                  {/* Image Controls */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-50 transition-opacity group">
                    {/* Featured Star - Always Visible */}
                    <div className="absolute top-2 left-2">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSetFeatured(image.id);
                        }}
                        disabled={updatingFeatured === image.id}
                        className={`p-1 rounded shadow-md transition-colors ${
                          localFeaturedImageId === image.id
                            ? 'bg-yellow-500 text-white'
                            : 'bg-white text-gray-600 hover:text-yellow-500'
                        } ${
                          updatingFeatured === image.id ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        title={
                          updatingFeatured === image.id
                            ? 'Updating...'
                            : localFeaturedImageId === image.id 
                            ? 'Remove featured (click to unset)' 
                            : 'Set as featured image'
                        }
                        type="button"
                      >
                        {updatingFeatured === image.id ? (
                          <LoadingOutlined className="text-sm animate-spin" />
                        ) : localFeaturedImageId === image.id ? (
                          <StarFilled className="text-sm" />
                        ) : (
                          <StarOutlined className="text-sm" />
                        )}
                      </button>
                    </div>
                    
                    {/* Other Controls - Show on Hover */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity space-x-1">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleViewImage(image.url, image.title || `Image ${index + 1}`);
                        }}
                        className="p-1 bg-white text-gray-600 hover:text-primary-500 rounded shadow-md transition-colors"
                        title="View full size"
                        type="button"
                      >
                        <EyeOutlined className="text-sm" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDelete(image.id);
                        }}
                        className="p-1 bg-white text-gray-600 hover:text-red-500 rounded shadow-md transition-colors"
                        title="Delete image"
                        type="button"
                      >
                        <DeleteOutlined className="text-sm" />
                      </button>
                    </div>
                    
                    {/* Drag Handle */}
                    <div className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="p-1 bg-white text-gray-600 rounded shadow-md cursor-grab active:cursor-grabbing" title="Drag to reorder">
                        <DragOutlined className="text-sm" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Image Info */}
                <div className="p-3">
                  <div className="space-y-2">
                    <div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-9 truncate" title={image.title}>
                          {image.title || `Image ${index + 1}`}
                        </p>
                      </div>
                      {image.description && (
                        <p className="text-xs text-gray-6 overflow-hidden" 
                           style={{
                             display: '-webkit-box',
                             WebkitLineClamp: 2,
                             WebkitBoxOrient: 'vertical',
                             overflow: 'hidden'
                           }}
                           title={image.description}>
                          {image.description}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gray-5">
                      <span>Position: {index + 1}</span>
                      <span>{image.id.slice(0, 8)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {images.length === 0 && !uploading && (
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">📷</div>
          <p className="text-gray-6">No images uploaded yet</p>
          <p className="text-sm text-gray-5">Upload some images to get started</p>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
